import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation.js';
import Footer from '../components/Footer.jsx';
import { brandText } from '../components/BrandText.jsx';
import { localizePath } from '../utils/localizedRoutes.js';
import { teamAssets } from '../assets/team/index.js';
import teamProfiles from '../profile/index.cjs';

const { teamMembers, getMember } = teamProfiles;

const TeamMemberPage = () => {
  const { member: memberSlug } = useParams();
  const { currentLang, getPageTranslations } = useTranslation('team');
  const page = getPageTranslations('team');
  const member = getMember(memberSlug);

  if (!member) {
    return <Navigate to={localizePath('/team', currentLang)} replace />;
  }

  const t = member.profiles[currentLang] || member.profiles.en;
  const assets = teamAssets[member.slug];
  const positions = t.experience.positions;

  const downloads = [
    {
      href: assets.cv.de,
      label: t.downloads.deLabel,
      description: t.downloads.deDescription,
      filename: t.downloads.files.de
    },
    {
      href: assets.cv.en,
      label: t.downloads.enLabel,
      description: t.downloads.enDescription,
      filename: t.downloads.files.en
    }
  ];

  return (
    <div className="min-h-screen bg-bg-main text-text-primary">
      <section className="pt-20 pb-8 md:pt-24 md:pb-10">
        <div className="max-w-6xl mx-auto px-4">
          <nav className="team-switcher" aria-label={page.profile?.membersLabel}>
            <Link to={localizePath('/team', currentLang)} className="team-switcher__back">
              <ArrowLeft size={14} aria-hidden="true" />
              <span>{page.profile?.overview}</span>
            </Link>
            <div className="team-switcher__members">
              {teamMembers.map((entry) => (
                <Link
                  key={entry.slug}
                  to={localizePath(`/team/${entry.slug}`, currentLang)}
                  className={`team-switcher__item ${entry.slug === member.slug ? 'team-switcher__item--active' : ''}`}
                  aria-current={entry.slug === member.slug ? 'page' : undefined}
                >
                  {entry.displayName}
                </Link>
              ))}
            </div>
          </nav>

          <div className="homepage-card p-5 md:p-6">
            <div className="about-hero-grid">
              <div className="about-hero-copy">
                {t.hero.kicker && <p className="profile-role">{brandText(t.hero.kicker)}</p>}
                <h1 className="profile-name">
                  {t.hero.title}
                </h1>
                <p className="text-lg md:text-xl text-text-secondary font-semibold">
                  {t.hero.subtitle}
                </p>
                {t.summary && (
                  <p className="text-text-muted leading-relaxed mt-3 max-w-3xl">
                    {t.summary}
                  </p>
                )}

                <div className="about-download-panel">
                  <div>
                    <h2 className="text-lg font-black tracking-[-0.025em] text-text-primary">
                      {t.downloads.title}
                    </h2>
                  </div>

                  <div className="about-download-actions">
                    {downloads.map((download) => (
                      <a
                        key={download.filename}
                        href={download.href}
                        download={download.filename}
                        className="about-download-card"
                      >
                        <span className="block font-black text-text-primary">{download.label}</span>
                        <span className="block text-xs text-text-secondary mt-0.5">{download.description}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div className="homepage-photo-frame about-photo-frame">
                <img
                  src={assets.photo}
                  alt={t.hero.imageAlt}
                  className="about-photo"
                />
              </div>
            </div>

            <div className="homepage-rule my-5" />

            <h2 className="text-xl font-black tracking-[-0.025em] text-text-primary mb-4">
              {t.personal.title}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <tbody>
                  {t.personal.rows.map((row) => (
                    <tr key={row.key} className="border-b border-border last:border-b-0">
                      <td className="py-3 pr-4 sm:pr-6 font-bold min-w-[7.5rem] sm:min-w-[200px] text-text-secondary">{row.label}</td>
                      <td className="py-3 text-text-primary max-sm:hyphens-auto max-sm:break-words max-sm:[hyphenate-limit-chars:12_5_5] max-sm:[-webkit-hyphenate-limit-before:5] max-sm:[-webkit-hyphenate-limit-after:5]">
                        {row.links ? row.links.map((link) => (
                          <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="profile-link">{link.text}</a>
                        )) : brandText(row.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="homepage-rule my-6" />

            <div className="grid lg:grid-cols-[240px_1fr] gap-6 lg:gap-10">
              <div>
                <h3 className="text-xl font-black text-text-primary">{t.education.title}</h3>
              </div>
              <div className="space-y-5">
                {t.education.entries.map((entry) => (
                  <div key={`${entry.period}-${entry.field}`} className="text-text-secondary leading-relaxed">
                    <span className="font-bold text-primary">{entry.period}</span> {entry.field}<br />
                    {entry.institution && (<>{entry.institution}<br /></>)}
                    <span className="font-bold text-text-primary">{entry.degree}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="homepage-rule my-6" />

            <div className="grid lg:grid-cols-[240px_1fr] gap-6 lg:gap-10">
              <div>
                <h3 className="text-xl font-black text-text-primary">{t.skills.title}</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {t.skills.groups.map((skill) => (
                  <div key={skill.title} className="bg-bg-hover/70 rounded-2xl border border-border p-5">
                    <h4 className="font-black text-text-primary mb-2">{skill.title}</h4>
                    <p className="text-text-secondary leading-relaxed text-sm">{skill.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="homepage-card p-6 md:p-8 mt-8">
            <div className="editorial-kicker mb-6" aria-hidden="true" />
            <h2 className="text-3xl md:text-4xl font-black tracking-[-0.04em] text-text-primary mb-8">
              {t.experience.title}
            </h2>

            <div className="space-y-8">
              {positions.map((position, index) => (
                <div key={`${position.period}-${position.title}`} className={`grid md:grid-cols-[180px_1fr] gap-4 ${index < positions.length - 1 ? 'border-b border-border pb-8' : ''}`}>
                  <p className="text-text-muted text-sm font-bold">{position.period}</p>
                  <div>
                    <h3 className="text-xl font-black tracking-[-0.02em] text-text-primary mb-3">{position.title}</h3>
                    {position.technologies && (
                      <p className="text-text-secondary mb-2"><span className="font-bold text-text-primary">{t.experience.techLabel}:</span> {position.technologies}</p>
                    )}
                    <div className="space-y-2">
                      {(position.description || []).map((desc, descIndex) => (
                        <p key={descIndex} className="text-text-secondary leading-relaxed">{desc}</p>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="profile-contact">
        <div>
          <h2 className="site-heading">{t.cta.title}</h2>
          <p className="profile-contact__mail">
            <a href={`mailto:${t.cta.email}`}>{brandText(t.cta.email)}</a>
          </p>
        </div>
        <a
          href={`mailto:${t.cta.email}?subject=${encodeURIComponent(t.cta.subject)}`}
          className="homepage-button-primary"
        >
          {t.cta.button}
        </a>
      </section>

      <Footer />
    </div>
  );
};

export default TeamMemberPage;
